// ─── ReminderModal.jsx — Comprehensive reminder creator ───────────────────────
import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS, SPRING } from "../lib/theme.js";
import { LS } from "../lib/storage.js";
import { newReminder, saveReminder, requestNotifPermission } from "../lib/reminders.js";
import { Btn, BottomSheet, Label } from "./ui.jsx";

const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function getStoredBrands() {
  try {
    const raw = localStorage.getItem("sf_brands");
    if (raw) {
      const p = JSON.parse(raw);
      if (Array.isArray(p) && p.length > 0) return p.map(b => ({ key:String(b.key||"main"), label:String(b.label||b.key||"Brand") }));
    }
  } catch {}
  return [{ key:"main", label:"Brand 1" }];
}

// Context types a reminder can be linked to
const CONTEXT_TYPES = [
  { id:"none",      icon:"🔔", label:"General",            desc:"Standalone reminder" },
  { id:"recurring", icon:"🔄", label:"Recurring Transaction", desc:"Income or expense series" },
  { id:"decision",  icon:"🌈", label:"Planner Decision",   desc:"Linked to a decision" },
  { id:"order",     icon:"📦", label:"Supplier Order",     desc:"Linked to an order" },
  { id:"calendar",  icon:"📅", label:"Cash Calendar",      desc:"Specific month/transaction" },
];

function ReminderModalInner({
  open, onClose,
  attachedTo = null,   // pre-set from card context (legacy usage)
  initialTitle = "",
  globalMode = false,  // true when opened from navbar (shows full context picker)
  data = {},           // { recurringExpenses, recurringIncome, decisions, orders, months }
  dataKey = "main",
}) {
  const T = useTheme();
  const safeTitle = typeof initialTitle === "string" ? initialTitle : String(initialTitle || "");
  const [form, setForm] = useState(() => newReminder({ title: safeTitle, attachedTo }));
  const [notifGranted, setNotifGranted] = useState(false);
  const [contextType, setContextType] = useState(attachedTo?.kind || "none");
  const [selectedItemId, setSelectedItemId] = useState(String(attachedTo?.id || ""));
  const [itemSearch, setItemSearch] = useState("");
  const [saved, setSaved] = useState(null); // holds confirmation data after save

  useEffect(() => {
    if (!open) return;
    const at = attachedTo || null;
    const title = typeof initialTitle === "string" ? initialTitle : String(initialTitle || "");
    setForm(newReminder({ title, attachedTo: at }));
    setContextType(at?.kind || "none");
    setSelectedItemId(String(at?.id || ""));
    setItemSearch("");
    setSaved(null);
    if ("Notification" in window) setNotifGranted(Notification.permission === "granted");
  }, [open, initialTitle]);

  if (!open) return null;

  // Guard: ensure form is properly initialized before rendering
  if (!form || typeof form !== "object") return null;

  // Safety: ensure all rendered form values are primitives to prevent React error #310
  const safeForm = {
    title: String(form.title ?? ""),
    message: String(form.message ?? ""),
    date: String(form.date ?? new Date().toISOString().split("T")[0]),
    time: String(form.time ?? "09:00"),
    type: String(form.type ?? "once"),
    priority: String(form.priority ?? "medium"),
    brandScope: typeof form.brandScope === "string" ? form.brandScope : Array.isArray(form.brandScope) ? form.brandScope : "own",
    weekday: Number(form.weekday ?? 1),
    dayOfMonth: Number(form.dayOfMonth ?? 1),
  };

  const currentBrand = String(LS.get(LS.BRAND, "main"));
  const allBrands = getStoredBrands();
  const otherBrands = allBrands.filter(b => b.key !== currentBrand);

  function patch(fields) { setForm(f => ({ ...f, ...fields })); }

  // Build list of linkable items based on contextType
  const contextItems = useMemo(() => {
    if (!globalMode) return [];
    const q = String(itemSearch || "").toLowerCase();
    if (contextType === "recurring") {
      const exp = (data.recurringExpenses || []).map(r => ({ id:r.id, label:r.what||r.name||"Recurring expense", sub:"Expense · "+r.category, icon:"💸" }));
      const inc = (data.recurringIncome || []).map(r => ({ id:r.id, label:r.what||r.name||"Recurring income", sub:"Income · "+r.category, icon:"💰" }));
      return [...inc, ...exp].filter(i => !q || i.label.toLowerCase().includes(q));
    }
    if (contextType === "decision") {
      return (data.decisions || [])
        .filter(d => !q || (d.name||"").toLowerCase().includes(q))
        .map(d => ({ id:d.id, label:d.name||"Unnamed decision", sub:d.category||"", icon:"🌈" }));
    }
    if (contextType === "order") {
      return (data.orders || [])
        .filter(o => !q || (o.number||o.supplier||"").toLowerCase().includes(q))
        .map(o => ({ id:o.id, label:`${o.number||"Order"} ${o.supplier?`· ${o.supplier}`:""}`, sub:o.amount?`$${o.amount.toLocaleString()}`:"", icon:"📦" }));
    }
    return [];
  }, [contextType, itemSearch, data]);

  // When a context item is selected, auto-fill title
  function selectItem(item) {
    setSelectedItemId(item.id);
    if (!safeForm.title.trim()) patch({ title: `Reminder: ${item.label}` });
    patch({ attachedTo: { kind: contextType, id: item.id, label: item.label } });
  }

  function handleSave() {
    if (!safeForm.title.trim()) return;
    const reminder = {
      ...form,
      ownBrand: currentBrand,
      attachedTo: contextType !== "none" && selectedItemId
        ? { kind: contextType, id: selectedItemId }
        : form.attachedTo || null,
    };
    saveReminder(reminder);
    // Build confirmation message
    const dateStr = safeForm.date || "today";
    const timeStr = safeForm.time || "09:00";
    const typeLabel = safeForm.type === "once" ? `on ${dateStr}` : safeForm.type === "daily" ? "every day" : safeForm.type === "weekly" ? "every week" : "every month";
    setSaved({ dateStr, timeStr, typeLabel });
    // Auto-close after 3 seconds
    setTimeout(() => { setSaved(null); onClose(); }, 3000);
  }

  async function handleRequestPermission() {
    const granted = await requestNotifPermission();
    setNotifGranted(granted);
  }

  const inp = {
    background:"transparent",
    border:`1px solid ${T.border}`,
    borderRadius:RADIUS.sm,
    color:T.text,
    fontSize:13,
    padding:"8px 10px",
    outline:"none",
    fontFamily:FONT,
    width:"100%",
    boxSizing:"border-box",
    transition:"border-color 0.15s",
  };

  const lbl = {
    fontSize:10, fontWeight:700, color:T.textSub,
    textTransform:"uppercase", letterSpacing:0.5,
    marginBottom:6, display:"block",
  };

  const pill = (active, color) => ({
    padding:"5px 14px", borderRadius:RADIUS.pill, fontSize:12,
    fontFamily:FONT, cursor:"pointer",
    background:active ? (color||T.accent)+"18" : "transparent",
    color:active ? (color||T.accent) : T.textSub,
    border:`1px solid ${active ? (color||T.accent) : T.border}`,
    fontWeight:active ? 700 : 400, transition:"all 0.15s",
  });

  return (
    <BottomSheet open={open} onClose={onClose} title="🔔 New Reminder" height="90vh"
      footer={saved ? <Btn onClick={()=>{setSaved(null);onClose();}} color={T.accent} style={{flex:1}}>Done</Btn> : <>
        <Btn onClick={onClose} variant="outline" color={T.textSub} style={{flex:1}}>Cancel</Btn>
        <Btn onClick={handleSave} color={T.accent} disabled={!safeForm.title.trim()} style={{flex:2}}>🔔 Save Reminder</Btn>
      </>}>
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

        {/* Confirmation after save */}
        {saved && (
          <div style={{ textAlign:"center", padding:"32px 16px" }}>
            <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
            <div style={{ fontSize:18, fontWeight:700, color:T.text, marginBottom:8 }}>Reminder Set!</div>
            <div style={{ fontSize:14, color:T.textSub, lineHeight:1.6, marginBottom:16 }}>
              Your reminder has been set for <strong style={{ color:T.accent }}>{saved.dateStr}</strong> at <strong style={{ color:T.accent }}>{saved.timeStr}</strong>
              {saved.typeLabel !== `on ${saved.dateStr}` && <>, repeating <strong style={{ color:T.accent }}>{saved.typeLabel}</strong></>}.
            </div>
            <div style={{ fontSize:13, color:T.textSub, lineHeight:1.6 }}>
              You'll receive a notification on this platform.
              {notifGranted && <><br/>Browser notifications are enabled.</>}
            </div>
            <div style={{ fontSize:12, color:T.textMuted, marginTop:12, lineHeight:1.5, padding:"10px 14px", background:T.surface, borderRadius:RADIUS.md }}>
              Coming soon: WhatsApp, email, and SMS reminders.
            </div>
          </div>
        )}

        {/* Form — hidden when showing confirmation */}
        {!saved && <>
        {/* Browser notification permission banner */}
        {!notifGranted && (
          <div style={{ background:T.orange+"14", border:`1px solid ${T.orange}44`,
            borderRadius:RADIUS.md, padding:"10px 14px",
            display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:T.orange }}>Enable browser notifications</div>
              <div style={{ fontSize:11, color:T.textSub, marginTop:2 }}>Get alerted even when the app is in background</div>
            </div>
            <button onClick={handleRequestPermission} style={{
              background:T.orange, border:"none", borderRadius:RADIUS.sm,
              color:"#fff", fontSize:11, padding:"5px 12px", cursor:"pointer", fontFamily:FONT, fontWeight:700, flexShrink:0,
            }}>Enable</button>
          </div>
        )}

        {/* Title */}
        <div>
          <label style={lbl}>Reminder Title *</label>
          <input value={safeForm.title} onChange={e=>patch({title:e.target.value})}
            placeholder="e.g. Pay supplier deposit, Review Q4 targets…"
            style={inp} autoFocus
            onFocus={e=>e.target.style.borderColor=T.accent}
            onBlur={e=>e.target.style.borderColor=T.border}/>
        </div>

        {/* Notes */}
        <div>
          <label style={lbl}>Notes <span style={{fontWeight:400,color:T.textSub}}>(optional)</span></label>
          <textarea value={safeForm.message} onChange={e=>patch({message:e.target.value})}
            placeholder="Additional context, links, or instructions…"
            rows={2}
            style={{ ...inp, resize:"vertical" }}
            onFocus={e=>e.target.style.borderColor=T.accent}
            onBlur={e=>e.target.style.borderColor=T.border}/>
        </div>

        {/* Context — link to something */}
        {globalMode && (
          <div>
            <label style={lbl}>Link to <span style={{fontWeight:400,color:T.textSub}}>(optional)</span></label>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
              {CONTEXT_TYPES.map(ct => (
                <button key={ct.id} onClick={()=>{setContextType(ct.id);setSelectedItemId("");setItemSearch("");}}
                  style={{
                    ...pill(contextType===ct.id),
                    display:"flex", alignItems:"center", gap:5,
                  }}>
                  <span style={{fontSize:14}}>{ct.icon}</span>
                  <span>{ct.label}</span>
                </button>
              ))}
            </div>

            {/* Item picker for linked contexts */}
            {contextType !== "none" && contextType !== "calendar" && (
              <div style={{ background:T.surface, borderRadius:RADIUS.md, padding:12 }}>
                <div style={{ position:"relative", marginBottom:8 }}>
                  <span style={{ position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",fontSize:12,color:T.textSub,pointerEvents:"none" }}>🔍</span>
                  <input value={itemSearch} onChange={e=>setItemSearch(e.target.value)}
                    placeholder={`Search ${CONTEXT_TYPES.find(c=>c.id===contextType)?.label||""}…`}
                    style={{ ...inp, paddingLeft:28, fontSize:12 }}/>
                </div>
                <div style={{ maxHeight:160, overflowY:"auto", display:"flex", flexDirection:"column", gap:4 }}>
                  {contextItems.length === 0 && (
                    <div style={{ padding:"12px 0", color:T.textSub, fontSize:12, textAlign:"center" }}>
                      No {CONTEXT_TYPES.find(c=>c.id===contextType)?.label||"items"} found
                    </div>
                  )}
                  {contextItems.map(item => (
                    <button key={item.id} onClick={()=>selectItem(item)}
                      style={{
                        display:"flex", alignItems:"center", gap:8,
                        padding:"8px 10px", borderRadius:RADIUS.sm,
                        background:selectedItemId===item.id ? T.accent+"18" : "transparent",
                        border:`1px solid ${selectedItemId===item.id ? T.accent : T.border}`,
                        cursor:"pointer", textAlign:"left", fontFamily:FONT,
                        transition:"all 0.12s",
                      }}>
                      <span style={{fontSize:16,flexShrink:0}}>{item.icon}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{ fontSize:12, fontWeight:600, color:selectedItemId===item.id?T.accent:T.text,
                          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.label}</div>
                        {item.sub && <div style={{ fontSize:10, color:T.textSub }}>{item.sub}</div>}
                      </div>
                      {selectedItemId===item.id && <span style={{color:T.accent,fontSize:14,flexShrink:0}}>✓</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {contextType === "calendar" && (
              <div style={{ padding:"10px 12px", background:T.surface, borderRadius:RADIUS.md, fontSize:12, color:T.textSub }}>
                📅 This reminder will appear as a general reminder. For transaction-specific reminders, open the transaction row in Cash Calendar and tap the 🔔 bell icon.
              </div>
            )}

            {contextType !== "none" && selectedItemId && (
              <div style={{ marginTop:8, padding:"6px 10px", background:T.accent+"0a", borderRadius:RADIUS.sm, fontSize:11, color:T.accent, border:`1px solid ${T.accent}22` }}>
                ✓ Reminder linked — will navigate to this {CONTEXT_TYPES.find(c=>c.id===contextType)?.label} when triggered
              </div>
            )}
          </div>
        )}

        {/* Legacy: show attached badge when opened from a card */}
        {!globalMode && attachedTo && (
          <div style={{ background:T.accent+"12", border:`1px solid ${T.accent}33`,
            borderRadius:RADIUS.md, padding:"8px 12px", fontSize:12, color:T.textSub }}>
            📎 Attached to: <strong style={{color:T.accent}}>{String(attachedTo.kind||"item")}</strong> — {String(attachedTo.label||attachedTo.id||"")}
          </div>
        )}

        {/* Repeat / frequency */}
        <div>
          <label style={lbl}>Repeat</label>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            {[["once","One-time"],["daily","Daily"],["weekly","Weekly"],["monthly","Monthly"]].map(([v,l]) => (
              <button key={v} onClick={()=>patch({type:v})} style={pill(safeForm.type===v)}>{l}</button>
            ))}
          </div>
        </div>

        {/* Date + Time */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div>
            <label style={lbl}>{safeForm.type==="once" ? "Date" : "Start Date"}</label>
            <input type="date" value={safeForm.date} onChange={e=>patch({date:e.target.value})} style={inp}
              onFocus={e=>e.target.style.borderColor=T.accent}
              onBlur={e=>e.target.style.borderColor=T.border}/>
          </div>
          <div>
            <label style={lbl}>Time</label>
            <input type="time" value={safeForm.time} onChange={e=>patch({time:e.target.value})} style={inp}
              onFocus={e=>e.target.style.borderColor=T.accent}
              onBlur={e=>e.target.style.borderColor=T.border}/>
          </div>
        </div>

        {/* Weekly: pick weekday */}
        {safeForm.type === "weekly" && (
          <div>
            <label style={lbl}>Day of Week</label>
            <div style={{ display:"flex", gap:4 }}>
              {WEEKDAYS.map((d,i) => (
                <button key={i} onClick={()=>patch({weekday:i})}
                  style={{ flex:1, padding:"5px 0", borderRadius:RADIUS.sm, fontSize:11,
                    fontFamily:FONT, cursor:"pointer", border:`1px solid ${safeForm.weekday===i?T.accent:T.border}`,
                    background:safeForm.weekday===i?T.accent+"18":"transparent",
                    color:safeForm.weekday===i?T.accent:T.textSub,
                    fontWeight:safeForm.weekday===i?700:400 }}>{d}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Monthly: pick day of month */}
        {safeForm.type === "monthly" && (
          <div>
            <label style={lbl}>Day of Month</label>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <input type="number" min={1} max={31} value={safeForm.dayOfMonth}
                onChange={e=>patch({dayOfMonth:parseInt(e.target.value)||1})}
                style={{ ...inp, width:80 }}/>
              <span style={{ fontSize:12, color:T.textSub }}>of each month</span>
            </div>
          </div>
        )}

        {/* Priority indicator */}
        <div>
          <label style={lbl}>Priority</label>
          <div style={{ display:"flex", gap:6 }}>
            {[["low","Low",T.teal],["medium","Medium",T.orange],["high","High",T.red]].map(([v,l,c]) => (
              <button key={v} onClick={()=>patch({priority:v})}
                style={pill((safeForm.priority||"medium")===v, c)}>
                {v==="high"?"🔴":v==="medium"?"🟡":"🟢"} {l}
              </button>
            ))}
          </div>
        </div>

        {/* Brand scope */}
        {otherBrands.length > 0 && (
          <div>
            <label style={lbl}>Visible to</label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              <button onClick={()=>patch({brandScope:"own"})} style={pill(safeForm.brandScope==="own")}>This brand only</button>
              <button onClick={()=>patch({brandScope:"all"})} style={pill(safeForm.brandScope==="all")}>All brands</button>
              {otherBrands.map(b => {
                const isSel = Array.isArray(safeForm.brandScope) && safeForm.brandScope.includes(b.key);
                return (
                  <button key={b.key} onClick={()=>{
                    const curr = Array.isArray(safeForm.brandScope) ? safeForm.brandScope : [];
                    patch({brandScope: isSel ? curr.filter(k=>k!==b.key) : [...curr, b.key]});
                  }} style={pill(isSel)}>
                    {isSel?"✓ ":""}{String(b.label||b.key||"")}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        </>}

      </div>
    </BottomSheet>
  );
}

// Wrapper that catches render errors gracefully
class ReminderErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(e) { console.error("ReminderModal error:", e); }
  render() { return this.state.hasError ? null : this.props.children; }
}

export default function ReminderModal(props) {
  return <ReminderErrorBoundary key={String(props.open)}><ReminderModalInner {...props}/></ReminderErrorBoundary>;
}
